<template>
	<section v-if="isDebugMode" data-test="capture-evidence-panel" v-test="'capture-evidence-panel'" class="capture-evidence mt-2">
		<button
			v-if="!hideToggle"
			type="button"
			data-test="capture-evidence-toggle"
			v-test="'capture-evidence-toggle'"
			class="capture-evidence-toggle text-body-2"
			:aria-expanded="expanded"
			@click="toggle">
			<span>Capture evidence</span>
			<span aria-hidden="true">{{ expanded ? "−" : "+" }}</span>
		</button>
		<div v-if="expanded && !hideContent" class="pt-2">
			<div v-if="loading" data-test="capture-evidence-loading" class="text-body-2 text-medium-emphasis">
				Loading capture evidence…
			</div>
			<v-alert v-else-if="error" data-test="capture-evidence-error" type="error" density="compact" variant="tonal">{{
				error
			}}</v-alert>
			<div v-else-if="!summary" data-test="capture-evidence-missing" class="text-body-2 text-medium-emphasis">
				No capture evidence is available.
			</div>
			<div v-else data-test="capture-evidence-summary" class="d-flex flex-column ga-2 text-body-2">
				<div data-test="capture-evidence-status" v-test="'capture-evidence-status'">
					<strong>Status:</strong> {{ summary.processing }}
				</div>
				<div v-if="captureMethod" data-test="capture-evidence-method" v-test="'capture-evidence-method'">
					<strong>Method:</strong> {{ captureMethod }}
				</div>
				<div v-if="capturePlatform" data-test="capture-evidence-platform" v-test="'capture-evidence-platform'">
					<strong>Platform:</strong> {{ capturePlatform }}
				</div>
				<div v-if="summary.originalMimeType || summary.originalByteSize" data-test="capture-evidence-original">
					<strong>Original:</strong> {{ summary.originalMimeType || "unknown type"
					}}<span v-if="summary.originalByteSize"> · {{ formatBytes(summary.originalByteSize) }}</span>
				</div>
				<div v-if="summary.widthPx || summary.heightPx || summary.dimensions" data-test="capture-evidence-dimensions">
					<strong>Dimensions:</strong> {{ summary.widthPx ?? summary.dimensions?.width ?? "?" }} ×
					{{ summary.heightPx ?? summary.dimensions?.height ?? "?"
					}}<span v-if="summary.orientation ?? summary.dimensions?.orientation">
						· {{ summary.orientation ?? summary.dimensions?.orientation }}</span
					>
				</div>
				<div v-if="summary.sensor || summary.nativeSpatial" data-test="capture-evidence-sensor">
					<strong>Sensor:</strong> {{ summary.nativeSpatial?.status || summary.sensor?.status || "available"
					}}<span v-if="summary.nativeSpatial?.sampleCount != null"> · {{ summary.nativeSpatial.sampleCount }} samples</span
					><span v-if="summary.nativeSpatial?.mapping"> · {{ summary.nativeSpatial.mapping }}</span>
				</div>
				<div v-if="summary.fallbackReason" data-test="capture-evidence-fallback">
					<strong>Fallback:</strong> {{ summary.fallbackReason }}
				</div>
				<div v-if="summary.limitations?.length" data-test="capture-evidence-limitations">
					<strong>Notes:</strong> {{ summary.limitations.join(" ") }}
				</div>
				<div v-if="summary.sourceType === 'recording' && summary.samples?.length" data-test="capture-evidence-timeline">
					<label for="capture-evidence-sample">Sample</label>
					<select id="capture-evidence-sample" v-model="selectedSampleId">
						<option v-for="sample in summary.samples" :key="sample.id" :value="sample.id">
							{{ formatSample(sample) }}
						</option>
					</select>
					<div v-if="selectedSample" class="text-caption text-medium-emphasis mt-1">
						{{
							selectedSample.timingDeltaMs != null
								? `Timing delta: ${selectedSample.timingDeltaMs} ms`
								: "Timing delta unavailable"
						}}
					</div>
					<div
						v-if="samplesWithDepthThumbnail.length"
						data-test="capture-evidence-sample-scrubber"
						v-test="'capture-evidence-sample-scrubber'"
						class="d-flex flex-column ga-1 mt-2">
						<div class="d-flex align-center ga-2">
							<input
								type="range"
								min="0"
								:max="samplesWithDepthThumbnail.length - 1"
								step="1"
								:value="currentSampleIndex"
								class="flex-grow-1"
								data-test="capture-evidence-scrubber-range"
								@input="onScrub($event)" />
							<span class="text-caption text-medium-emphasis text-no-wrap">
								{{ currentSampleIndex + 1 }} / {{ samplesWithDepthThumbnail.length }}
							</span>
						</div>
						<div class="filmstrip" data-test="capture-evidence-sample-thumbnails" v-test="'capture-evidence-sample-thumbnails'">
							<button
								v-for="sample in samplesWithDepthThumbnail"
								:key="sample.id"
								:ref="(el) => setThumbnailRef(sample.id, el)"
								type="button"
								class="thumbnail-button"
								:class="{ 'thumbnail-button--selected': sample.id === selectedSampleId }"
								:title="formatSample(sample)"
								@click="selectSample(sample.id)">
								<img :src="sample.overlay?.depthUrl ?? undefined" :alt="`Depth preview for ${formatSample(sample)}`" />
							</button>
						</div>
					</div>
				</div>
				<div v-if="selectedSample?.depth" data-test="capture-evidence-scale">
					<strong>Depth:</strong> {{ selectedSample.depth.accuracy || "accuracy unavailable"
					}}<span v-if="selectedSample.depth.metricScaleKnown === false"> · metric scale unknown</span
					><span v-else-if="selectedSample.depth.metricScaleKnown === true"> · metric scale reported</span>
				</div>
				<div class="d-flex flex-wrap ga-2">
					<a
						v-if="summary.originalUrl"
						data-test="capture-evidence-original-download"
						v-test="'capture-evidence-original-download'"
						:href="summary.originalUrl"
						target="_blank"
						rel="noopener"
						>Download original</a
					>
					<a
						v-for="artifact in downloadableArtifacts"
						:key="artifact.id"
						:href="artifact.url || undefined"
						target="_blank"
						rel="noopener"
						>{{ artifact.label }}</a
					>
					<button
						type="button"
						data-test="capture-evidence-metadata-export"
						v-test="'capture-evidence-metadata-export'"
						class="text-button"
						@click="exportMetadata">
						Export metadata
					</button>
					<button
						v-if="samplesWithDepthThumbnail.length"
						type="button"
						data-test="capture-evidence-depth-zip-download"
						v-test="'capture-evidence-depth-zip-download'"
						class="text-button"
						:disabled="zipDownloadState === 'preparing'"
						@click="downloadDepthImagesZip">
						{{ zipDownloadState === "preparing" ? "Preparing zip…" : "Download all depth images (zip)" }}
					</button>
				</div>
				<div
					v-if="zipDownloadError"
					data-test="capture-evidence-depth-zip-error"
					class="text-caption"
					style="color: rgb(var(--v-theme-error))">
					{{ zipDownloadError }}
				</div>
				<div v-if="selectedSample?.overlay?.depthUrl" data-test="capture-evidence-depth-preview" class="preview-wrap">
					<img :src="selectedSample.overlay.depthUrl" alt="Depth preview" />
					<img
						v-if="selectedSample.overlay.confidenceUrl"
						:src="selectedSample.overlay.confidenceUrl"
						alt="Depth confidence preview" />
					<div class="text-caption text-medium-emphasis">
						{{
							selectedSample.alignment === "verified_transform"
								? "Registered to the captured image"
								: selectedSample.alignmentReason || "Depth-to-image alignment is unavailable"
						}}
					</div>
				</div>
				<div
					v-if="
						selectedSample?.overlay &&
						selectedSample.overlay.imageUrl &&
						selectedSample.overlay.depthUrl &&
						selectedSampleOverlayAllowed
					"
					data-test="capture-evidence-overlay"
					class="overlay-wrap">
					<img :src="selectedSample.overlay.imageUrl" alt="Captured image" />
					<img
						:src="selectedSample.overlay.depthUrl"
						alt="Depth preview"
						class="overlay-depth"
						:style="{ opacity: overlayOpacity }" />
					<label class="text-caption"
						>Depth opacity <input v-model.number="overlayOpacity" type="range" min="0" max="1" step="0.05"
					/></label>
				</div>
				<div
					v-if="selectedSample?.overlay?.imageUrl && visibleProjectedPlanes.length > 0"
					data-test="capture-evidence-plane-overlay"
					v-test="'capture-evidence-plane-overlay'"
					class="overlay-wrap">
					<img :src="selectedSample.overlay.imageUrl" alt="Captured image" />
					<svg class="overlay-plane" :viewBox="planeOverlayViewBox" preserveAspectRatio="none">
						<polygon
							v-for="(plane, planeIndex) in visibleProjectedPlanes"
							:key="planeIndex"
							:points="planePolygonPoints(plane)"
							fill="rgba(45, 116, 255, 0.15)"
							stroke="#2d74ff"
							stroke-width="4" />
					</svg>
					<div class="text-caption text-medium-emphasis">
						Detected planes (ARCore Plane API): {{ visibleProjectedPlanes.length }}
					</div>
					<div
						v-for="(plane, planeIndex) in measuredProjectedPlanes"
						:key="planeIndex"
						data-test="capture-evidence-plane-size"
						class="text-caption">
						<strong>Size:</strong> {{ formatMeters(plane.orientedSize!.widthMeters) }} ×
						{{ formatMeters(plane.orientedSize!.heightMeters) }}
						<span v-if="plane.orientedSize!.source === 'axis_aligned_extent'" class="text-medium-emphasis">
							(axis-aligned estimate — no polygon recorded)</span
						>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
	import { computed, ref, watch } from "vue";
	import JSZip from "jszip";
	import api from "@/api";
	import { DOWNLOAD_FETCH_OPTIONS } from "@common-ui/media/downloadFile";
	import { triggerBlobDownload } from "@/helpers/download";
	import { getMessageFromError } from "@/helpers/utilities";
	import { isLoadedMeFeatureFlagEnabled, loadMeFeatureFlag } from "@/helpers/meFeatureFlags";
	import type {
		CaptureEvidenceSummary,
		CaptureEvidenceSample,
		CaptureEvidenceSourceType,
		CaptureEvidenceProjectedPlane
	} from "@/types/media/CaptureEvidence";

	const props = defineProps<{
		sourceId: string;
		sourceType: CaptureEvidenceSourceType;
		hideToggle?: boolean;
		hideContent?: boolean;
	}>();
	const isDebugQuery = computed(() => new URLSearchParams(window.location.search).get("debug") === "true");
	// LSP-18592 go-live: the inspector needs BOTH ?debug=true and the server's
	// me.config.featureFlags.capture_evidence; with the flag off (the default) it never renders or fetches.
	const captureEvidenceFlagEnabled = ref(isLoadedMeFeatureFlagEnabled("capture_evidence"));
	if (isDebugQuery.value && !captureEvidenceFlagEnabled.value) {
		void loadMeFeatureFlag("capture_evidence").then((enabled) => {
			captureEvidenceFlagEnabled.value = enabled;
		});
	}
	const isDebugMode = computed(() => isDebugQuery.value && captureEvidenceFlagEnabled.value);
	const expanded = defineModel<boolean>("expanded", { default: false });
	const loading = ref(false);
	const error = ref("");
	const summary = ref<CaptureEvidenceSummary | null>(null);
	const selectedSampleId = ref("");
	const overlayOpacity = ref(0.5);
	let requestSequence = 0;

	const selectedSample = computed(() => summary.value?.samples?.find((sample) => sample.id === selectedSampleId.value) ?? null);
	const captureMethod = computed(() => {
		const manifest = summary.value?.clientManifest;
		if (manifest && typeof manifest.method === "string") return manifest.method;
		if (manifest && typeof manifest.source === "string") return manifest.source;
		return summary.value?.captureMethod ?? null;
	});
	// Mobile manifests carry an explicit `platform` ("ios" | "android"); web's manifest has no
	// `platform` field at all but always has a `source` ("web-camera" | "file-picker") instead.
	const capturePlatform = computed(() => {
		const manifest = summary.value?.clientManifest;
		if (!manifest) return null;
		if (manifest.platform === "ios") return "iOS";
		if (manifest.platform === "android") return "Android";
		if (typeof manifest.source === "string") return "Web";
		return typeof manifest.platform === "string" ? manifest.platform : null;
	});
	const selectedSampleOverlayAllowed = computed(() => selectedSample.value?.alignment === "verified_transform");
	// A recording can carry dozens of throttled depth samples — clicking through the dropdown one at
	// a time to eyeball each one doesn't scale. This gives an at-a-glance thumbnail strip instead;
	// clicking a thumbnail just drives the same selectedSampleId the dropdown does.
	const samplesWithDepthThumbnail = computed(() => summary.value?.samples?.filter((sample) => sample.overlay?.depthUrl) ?? []);
	// LSP-18592: lets the scrub bar/thumbnail strip below drive the same selectedSampleId the
	// dropdown does, and scrolls the selected thumbnail into view as the user scrubs.
	const thumbnailEls = new Map<string, HTMLElement>();

	const currentSampleIndex = computed(() => {
		const index = samplesWithDepthThumbnail.value.findIndex((sample) => sample.id === selectedSampleId.value);
		return index === -1 ? 0 : index;
	});

	function setThumbnailRef(sampleId: string, el: Element | { $el?: Element } | null) {
		const element = el instanceof Element ? el : (el?.$el ?? null);
		if (element instanceof HTMLElement) thumbnailEls.set(sampleId, element);
		else thumbnailEls.delete(sampleId);
	}

	function selectSample(sampleId: string) {
		selectedSampleId.value = sampleId;
		thumbnailEls.get(sampleId)?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
	}

	function onScrub(event: Event) {
		const index = Number((event.target as HTMLInputElement).value);
		const sample = samplesWithDepthThumbnail.value[index];
		if (sample) selectSample(sample.id);
	}

	// The extractor writes one entry per plane detected at this sample's timestamp, so every plane
	// that projects in front of the camera is drawn. Planes recorded at the same instant share the
	// colour calibration, so the first visible one supplies the shared viewBox.
	const visibleProjectedPlanes = computed(() =>
		(selectedSample.value?.projectedPlanes ?? []).filter((plane) => plane.allCornersInFrontOfCamera)
	);
	const measuredProjectedPlanes = computed(() => visibleProjectedPlanes.value.filter((plane) => plane.orientedSize));
	const planeOverlayViewBox = computed(() => {
		const plane = visibleProjectedPlanes.value[0];
		return plane ? `0 0 ${plane.widthPx} ${plane.heightPx}` : "";
	});

	function planePolygonPoints(plane: CaptureEvidenceProjectedPlane): string {
		return plane.cornersPx.map(([x, y]) => `${x},${y}`).join(" ");
	}
	const downloadableArtifacts = computed(() =>
		(Array.isArray(summary.value?.artifacts) ? summary.value.artifacts : []).filter((artifact) => artifact.url)
	);

	async function loadSummary() {
		if (!expanded.value || !isDebugMode.value || !props.sourceId) return;
		const sequence = ++requestSequence;
		loading.value = true;
		error.value = "";
		try {
			const response = (await api.getCaptureEvidence(props.sourceType, props.sourceId)) as CaptureEvidenceSummary & {
				status?: CaptureEvidenceSummary["processing"];
				summary?: Partial<CaptureEvidenceSummary> | null;
			};
			const result = {
				...(response.summary ?? {}),
				...response,
				processing: response.status ?? response.processing
			} as CaptureEvidenceSummary;
			if (sequence !== requestSequence) return;
			summary.value = result;
			selectedSampleId.value = result.samples?.[0]?.id ?? "";
		} catch (loadError) {
			if (sequence === requestSequence && !isNotFound(loadError))
				error.value = `Unable to load capture evidence: ${getMessageFromError(loadError)}`;
		} finally {
			if (sequence === requestSequence) loading.value = false;
		}
	}

	function toggle() {
		expanded.value = !expanded.value;
	}

	// `immediate: true` so a content-only instance — created via v-if only once `expanded` is
	// already true (see MediaLightBox's Comments-column swap) — still loads on mount, since it
	// never sees a false→true transition of its own to react to.
	watch(
		expanded,
		(value) => {
			if (value) void loadSummary();
			else requestSequence++;
		},
		{ immediate: true }
	);

	function isNotFound(value: unknown) {
		if (!value || typeof value !== "object") return false;
		const candidate = value as { status?: unknown; statusCode?: unknown; response?: { status?: unknown } };
		return [candidate.status, candidate.statusCode, candidate.response?.status].some((status) => status === 404 || status === "404");
	}

	function formatBytes(value: number) {
		if (value < 1024) return `${value} B`;
		if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
		return `${(value / (1024 * 1024)).toFixed(1)} MB`;
	}

	function formatMeters(meters: number) {
		const inches = meters * 39.3701;
		return `${meters.toFixed(2)} m (${inches.toFixed(1)} in)`;
	}

	function formatSample(sample: CaptureEvidenceSample) {
		const time = sample.captureTimeMs == null ? "time unavailable" : `${(sample.captureTimeMs / 1000).toFixed(2)}s`;
		return `${sample.index == null ? "Sample" : `Sample ${sample.index + 1}`} · ${time}`;
	}

	function exportMetadata() {
		if (!summary.value) return;
		const payload = JSON.stringify(summary.value, null, 2);
		const url = URL.createObjectURL(new Blob([payload], { type: "application/json" }));
		const link = document.createElement("a");
		link.href = url;
		link.download = `capture-evidence-${props.sourceId}.json`;
		link.click();
		URL.revokeObjectURL(url);
	}

	const zipDownloadState = ref<"idle" | "preparing">("idle");
	const zipDownloadError = ref("");

	async function fetchArrayBufferOrThrow(url: string, label: string): Promise<ArrayBuffer> {
		let response: Response;
		try {
			response = await fetch(url, DOWNLOAD_FETCH_OPTIONS);
		} catch (fetchError) {
			throw new Error(`${label}: network request failed (${getMessageFromError(fetchError)})`);
		}
		if (!response.ok) throw new Error(`${label}: server returned ${response.status}`);
		return response.arrayBuffer();
	}

	async function downloadDepthImagesZip() {
		if (zipDownloadState.value === "preparing") return;
		zipDownloadState.value = "preparing";
		zipDownloadError.value = "";
		try {
			// LSP-18592: signed URLs expire 300s after they were issued (see withSignedUrls
			// server-side) — re-fetching here instead of reusing summary.value's possibly-minutes-old
			// URLs avoids the zip silently failing because the user looked at the panel a while
			// before clicking this button.
			const response = (await api.getCaptureEvidence(props.sourceType, props.sourceId)) as CaptureEvidenceSummary & {
				status?: CaptureEvidenceSummary["processing"];
				summary?: Partial<CaptureEvidenceSummary> | null;
			};
			// Same precedence loadSummary() uses: response's top-level fields win over response.summary's.
			const fresh = { ...(response.summary ?? {}), ...response } as CaptureEvidenceSummary;
			const freshSamples = (fresh.samples ?? []).filter((sample) => sample.overlay?.depthUrl);
			const zip = new JSZip();
			// Each sample's depth/confidence fetch is independent, so run them all concurrently
			// instead of one at a time — a recording can carry dozens of samples.
			const fetchTasks: Promise<void>[] = [];
			for (const sample of freshSamples) {
				const label = sample.index != null ? `sample-${sample.index + 1}` : sample.id;
				const depthUrl = sample.overlay?.depthUrl;
				const confidenceUrl = sample.overlay?.confidenceUrl;
				if (depthUrl) {
					fetchTasks.push(
						fetchArrayBufferOrThrow(depthUrl, `${label} depth image`).then((buffer) => {
							zip.file(`${label}-depth.png`, buffer);
						})
					);
				}
				if (confidenceUrl) {
					fetchTasks.push(
						fetchArrayBufferOrThrow(confidenceUrl, `${label} confidence image`).then((buffer) => {
							zip.file(`${label}-confidence.png`, buffer);
						})
					);
				}
			}
			if (!fetchTasks.length) {
				zipDownloadError.value = "No depth images were available to download.";
				return;
			}
			await Promise.all(fetchTasks);
			const zipBlob = await zip.generateAsync({ type: "blob" });
			triggerBlobDownload(zipBlob, `capture-evidence-depth-${props.sourceId}.zip`);
		} catch (downloadError) {
			zipDownloadError.value = `Unable to build the zip: ${getMessageFromError(downloadError)}`;
		} finally {
			zipDownloadState.value = "idle";
		}
	}

	watch(
		() => [props.sourceId, props.sourceType, isDebugMode.value],
		() => {
			requestSequence++;
			summary.value = null;
			error.value = "";
			selectedSampleId.value = "";
			if (expanded.value) void loadSummary();
		}
	);
</script>

<style scoped>
	.capture-evidence-toggle {
		display: flex;
		justify-content: space-between;
		width: 100%;
		padding: 4px 0;
		border: 0;
		background: transparent;
		color: inherit;
		text-align: left;
	}

	.text-button {
		padding: 0;
		border: 0;
		background: transparent;
		color: rgb(var(--v-theme-primary));
		text-decoration: underline;
	}

	.overlay-wrap {
		position: relative;
		max-width: 480px;
		overflow: hidden;
	}

	.preview-wrap {
		max-width: 480px;
	}

	.preview-wrap img {
		display: block;
		max-width: 100%;
		margin-top: 4px;
	}

	.overlay-wrap img {
		display: block;
		width: 100%;
	}

	.overlay-depth {
		position: absolute;
		inset: 0;
	}

	.overlay-plane {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
	}

	.filmstrip {
		display: flex;
		flex-wrap: nowrap;
		gap: 4px;
		overflow-x: auto;
		scroll-snap-type: x proximity;
		padding-bottom: 4px;
	}

	.thumbnail-button {
		flex-shrink: 0;
		padding: 0;
		border: 2px solid transparent;
		border-radius: 4px;
		background: transparent;
		cursor: pointer;
		line-height: 0;
		scroll-snap-align: center;
	}

	.thumbnail-button--selected {
		border-color: rgb(var(--v-theme-primary));
	}

	.thumbnail-button img {
		display: block;
		height: 200px;
		width: auto;
		max-width: 280px;
		object-fit: contain;
		border-radius: 2px;
	}
</style>
